import { Product } from '@shared/components/ui/Product';
import { UserCard } from '@shared/components/ui/usercard';
import { faArrowRight, faLocationXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePermissions } from '@shared/hooks/usePermissions';
import type { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getMovementBatchDisplayLabel, MovementBatchCell } from '@modules/Reports/components/MovementBatchCell';
import { renderMovementOrder, renderMovementTask } from '../components/display/renderShopFromEvent';
import { LocationMovementsModel } from '../models/LocationMovements';

// 🎯 Helper: formatea la fecha en formato DD/MM/AAAA H:M
const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

// Hook: define y expone las columnas de AG Grid para LocationMovements
export const useAgGridColumns = () => {
  const { t } = useTranslation('locationmovementsModule');
  const { getTeamType } = usePermissions();
  const hasTeamData = getTeamType() === 'tenant_3pl';

  const allColumnDefs = useMemo<ColDef<LocationMovementsModel>[]>(() => {
    const baseColumns: ColDef<LocationMovementsModel>[] = [
      {
        colId: 'product',
        field: 'product.name',
        headerName: t('table.product'),
        sortable: false,
        minWidth: 220,
        cellRenderer: ({ data }: { data: LocationMovementsModel }) =>
          data?.product ? (
            <Product
              src={data.product.image || ''}
              name={data.product.name || data.product.code || ''}
              code={data.product.code}
              id={data.product.id}
              enableLineClamp={false}
              showTooltip={true}
              showImageModal={true}
            />
          ) : null,
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'batch',
        field: 'batch.batch_number',
        headerName: t('table.batch'),
        sortable: false,
        minWidth: 280,
        suppressSizeToFit: true,
        cellStyle: { display: 'flex', alignItems: 'center' },
        cellRenderer: ({ data }: { data: LocationMovementsModel }) =>
          data ? <MovementBatchCell item={data} i18nNs="locationmovementsModule" /> : null,
        valueGetter: ({ data }) => (data ? getMovementBatchDisplayLabel(data) : ''),
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'qty',
        field: 'qty',
        headerName: t('table.amount'),
        sortable: true,
        type: 'numericColumn',
        minWidth: 120,
        maxWidth: 150,
        cellStyle: { textAlign: 'left' },
        valueGetter: ({ data }) => data?.qty ?? 0,
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'movement',
        field: 'from_location.code',
        headerName: t('table.movement'),
        sortable: false,
        minWidth: 250,
        cellRenderer: ({ data }: { data: LocationMovementsModel }) => {
          const fromCode = data?.from_location?.code;
          const toCode = data?.to_location?.code;

          return (
            <div className="flex items-center gap-2 w-full">
              <span className="text-sm font-medium uppercase text-title">
                {fromCode || <FontAwesomeIcon icon={faLocationXmark} className="w-3.5 h-3.5 opacity-65" />}
              </span>
              <FontAwesomeIcon icon={faArrowRight} className="w-3 h-3 text-muted-foreground flex-shrink-0" />
              <span className="text-sm font-medium uppercase text-title">
                {toCode || <FontAwesomeIcon icon={faLocationXmark} className="w-3.5 h-3.5 opacity-65" />}
              </span>
            </div>
          );
        },
        valueGetter: ({ data }) => {
          const from = data?.from_location?.code ?? '';
          const to = data?.to_location?.code ?? '';
          return `${from} → ${to}`;
        },
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'event',
        field: 'event.name',
        headerName: t('table.type'),
        sortable: false,
        minWidth: 160,
        cellRenderer: ({ data }: { data: LocationMovementsModel }) => {
          const eventKey = data?.event?.name ?? String(data?.event?.id ?? '');
          const eventName = t(`filters.options.${eventKey}`, {
            defaultValue: eventKey || '-',
          }).toString();

          return <span className="text-sm font-medium">{eventName}</span>;
        },
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'order',
        field: 'order.ref',
        headerName: t('table.order'),
        sortable: false,
        minWidth: 150,
        maxWidth: 200,
        cellStyle: { display: 'flex', alignItems: 'center' },
        cellRenderer: ({ data }: { data: LocationMovementsModel }) => (data ? renderMovementOrder(data) : null),
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'task',
        field: 'stock_location_order_id',
        headerName: t('table.task'),
        sortable: false,
        minWidth: 160,
        maxWidth: 220,
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'flex-start' },
        cellRenderer: ({ data }: { data: LocationMovementsModel }) => (data ? renderMovementTask(data) : null),
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'description',
        field: 'description',
        headerName: t('table.description'),
        sortable: false,
        minWidth: 160,
        flex: 1,
        cellRenderer: ({ data }: { data: LocationMovementsModel }) => {
          const text = data?.description?.trim();
          return <span className="text-xs text-title line-clamp-2 flex items-center h-full">{text || '-'}</span>;
        },
        valueGetter: ({ data }) => data?.description ?? '',
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'user',
        field: 'user.name',
        headerName: t('table.user'),
        sortable: false,
        minWidth: 200,
        cellRenderer: ({ data }: { data: LocationMovementsModel }) => {
          const name = data?.user?.name;
          const image = data?.user?.image;

          if (!name) {
            return <span className="text-sm text-complementary-text">{t('fallbacks.no_user')}</span>;
          }

          return (
            <UserCard name={name} email={data?.user?.email ?? ''} data-testid={`user-${data?.id}`} avatarUrl={image} />
          );
        },
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'created_at',
        field: 'created_at',
        headerName: t('table.date'),
        sortable: true,
        minWidth: 160,
        maxWidth: 200,
        cellRenderer: ({ data }: { data: LocationMovementsModel }) => {
          const formatted = formatDateTime(data?.created_at);
          return <span className="text-sm font-medium">{formatted}</span>;
        },
        valueGetter: ({ data }) => data?.created_at ?? '',
        suppressHeaderMenuButton: true,
      },
    ];

    // 🎯 COLUMNA: Cliente (team) - Solo si es tenant_3pl
    if (hasTeamData) {
      baseColumns.push({
        colId: 'team',
        field: 'team',
        headerName: t('table.client'),
        sortable: false,
        minWidth: 200,
        cellRenderer: ({ data }: { data: LocationMovementsModel }) => {
          return data?.team?.name ? (
            <UserCard name={data.team.name} email="" />
          ) : (
            <span className="text-sm text-complementary-text">-</span>
          );
        },
        valueGetter: ({ data }) => data?.team?.name ?? '',
        suppressHeaderMenuButton: true,
      });
    }

    return baseColumns;
  }, [t, hasTeamData]);

  return { allColumnDefs };
};
